import type { Recording, SessionPlan } from '../types.ts';
import type { AssemblyDocument, AssemblyRelation, Counterpart, OutputClip, SourceSpan } from './schema.ts';
import { parseManifest } from './manifest.ts';

const RATE = 48000;
const frame = (sec: number) => Math.round(sec * RATE);

/** Sorted keys make identities independent of the JSON writer and property insertion order. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

export async function contentDigest(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(value));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return `sha256:${Array.from(new Uint8Array(digest), x => x.toString(16).padStart(2, '0')).join('')}`;
}

/** Capture what was resolved, without upgrading inferred acoustic labels to reviewed decisions. */
export async function resolveAssembly(
  rec: Recording, plan: SessionPlan, release: AssemblyDocument['release'],
  options: { planProvenance?: 'captured' | 'resolved' } = {},
): Promise<AssemblyDocument> {
  if (plan.recordingId !== rec.id) throw new Error('Assembly recording does not match its plan');
  const declaredSourceEnd = frame(rec.nativeSec);
  const sourceEnd = Math.max(declaredSourceEnd, ...rec.speech.map(s => frame(s.srcEnd)));
  if (sourceEnd - declaredSourceEnd > RATE * 0.01) throw new Error('Invalid assembly: source exceeds declared duration');
  const clips: OutputClip[] = plan.timeline.map((seg, i) => {
    const common = { id: `clip:${i}:${frame(seg.startSec)}-${frame(seg.endSec)}`,
      startFrame: frame(seg.startSec), endFrame: frame(seg.endSec), kind: seg.kind };
    if (seg.kind === 'tone') return common;
    const island = rec.speech[seg.island];
    if (!island) throw new Error('Assembly references a missing speech island');
    return { ...common, island: seg.island,
      sourceStartFrame: frame(seg.srcStart ?? island.srcStart),
      sourceEndFrame: frame(seg.srcEnd ?? island.srcEnd) };
  });
  const speech = clips.filter(c => c.kind === 'speech');
  const replacements = clips.filter(c => c.kind === 'tone').map(c => {
    const before = [...speech].reverse().find(s => s.endFrame <= c.startFrame);
    const after = speech.find(s => s.startFrame >= c.endFrame);
    return { clip: c, start: before?.sourceEndFrame ?? 0, end: after?.sourceStartFrame ?? sourceEnd };
  });
  const gapSpans = rec.gaps.map(g => {
    const start = g.afterIsland === -1 ? 0 : frame(rec.speech[g.afterIsland]?.srcEnd ?? rec.nativeSec);
    return { start, end: Math.min(sourceEnd, start + frame(g.nativeSec)) };
  });
  const boundaries = new Set([0, sourceEnd]);
  for (const s of rec.speech) { boundaries.add(frame(s.srcStart)); boundaries.add(frame(s.srcEnd)); }
  for (const s of speech) { boundaries.add(s.sourceStartFrame!); boundaries.add(s.sourceEndFrame!); }
  for (const g of gapSpans) { boundaries.add(g.start); boundaries.add(g.end); }
  const analysis = rec.acousticAnalysis;
  const acoustic = (analysis?.regions ?? []).map(region => ({ ...region,
    start: Math.round(region.startFrame * RATE / analysis!.frameRate),
    end: Math.round(region.endFrame * RATE / analysis!.frameRate),
  }));
  for (const region of acoustic) { boundaries.add(region.start); boundaries.add(region.end); }
  const points = [...boundaries].filter(x => x >= 0 && x <= sourceEnd).sort((a, b) => a - b);
  const sourceSpans: SourceSpan[] = [];
  const relations: AssemblyRelation[] = [];
  for (let i = 0; i + 1 < points.length; i++) {
    const startFrame = points[i];
    const endFrame = points[i + 1];
    if (endFrame <= startFrame) continue;
    const island = rec.speech.findIndex(s => frame(s.srcStart) <= startFrame && frame(s.srcEnd) >= endFrame);
    const copies = speech.filter(c => c.sourceStartFrame! <= startFrame && c.sourceEndFrame! >= endFrame);
    // A dropped speech passage remains visibly omitted, even when room tone replaces the
    // whole sitting region around it. Otherwise a wide ribbon appears to transfer words.
    const replacement = island < 0 ? replacements.find(r => r.start <= startFrame && r.end >= endFrame) : undefined;
    const disposition = copies.length ? 'copied' : replacement ? 'replaced' : 'omitted';
    const measured = acoustic.find(region => region.start <= startFrame && region.end >= endFrame);
    const span: SourceSpan = {
      id: `source:${startFrame}-${endFrame}`, startFrame, endFrame,
      classification: analysis ? (measured && measured.classification !== 'unknown'
        ? `${measured.classification}-region` : 'unknown') : island >= 0 ? 'speech-region'
        : gapSpans.some(g => g.start <= startFrame && g.end >= endFrame) ? 'quiet-region' : 'unknown',
      classificationBasis: analysis ? 'acoustic-analysis' : 'legacy-segmentation',
      ...(analysis ? { confidence: measured?.confidence ?? null } : {}),
      ...(island >= 0 ? { island } : {}), disposition,
      reason: copies.length ? 'Retained at original speed' : replacement ? 'Replaced by generated room tone'
        : island >= 0 ? 'Speech excluded by the captured plan' : 'Source outside the assembled sit',
    };
    sourceSpans.push(span);
    if (copies.length) for (const c of copies) relations.push({ id: `copy:${span.id}:${c.id}`,
      kind: 'copy', sourceSpanIds: [span.id], outputClipIds: [c.id], exact: true });
    else if (!replacement) relations.push({ id: `omit:${span.id}`, kind: 'omit',
      sourceSpanIds: [span.id], outputClipIds: [], exact: false });
  }
  for (const r of replacements) {
    const sources = sourceSpans.filter(s => s.disposition === 'replaced' && s.startFrame >= r.start && s.endFrame <= r.end);
    relations.push({ id: `replacement:${r.clip.id}`, kind: sources.length ? 'replace' : 'insert',
      sourceSpanIds: sources.map(s => s.id), outputClipIds: [r.clip.id], exact: false });
  }
  const assetPaths = [rec.audio?.path, rec.toneProfile, ...rec.speech.flatMap(s =>
    (s.parts ?? []).flatMap(p => Object.values(p.files ?? {}).map(f => f?.path)))].filter(Boolean) as string[];
  const doc: AssemblyDocument = {
    schemaVersion: 1, digest: '', recipe: 'anchored-assembly-v1', recordingId: rec.id, release: { ...release },
    source: { frameRate: RATE, frameCount: sourceEnd,
      declaredNativeFrameCount: declaredSourceEnd,
      durationBasis: sourceEnd > declaredSourceEnd ? 'precise-segmentation-end' : 'manifest' },
    output: { frameRate: RATE, frameCount: frame(plan.actualSec) },
    provenance: { plan: options.planProvenance ?? 'resolved',
      sourceClassification: analysis ? 'acoustic-analysis' : 'legacy-segmentation',
      assetIdentity: assetPaths.every(p => /[a-f0-9]{64}/i.test(p)) ? 'content-addressed' : 'legacy-unverified' },
    recording: structuredClone(rec), plan: structuredClone(plan), sourceSpans, clips, relations,
  };
  const errors = validateAssembly(doc);
  if (errors.length) throw new Error(`Invalid assembly: ${errors.join('; ')}`);
  const { digest: _digest, ...body } = doc;
  doc.digest = await contentDigest(body);
  return doc;
}

export function validateAssembly(doc: AssemblyDocument): string[] {
  const errors: string[] = [];
  if (doc.schemaVersion !== 1) errors.push('unsupported assembly schema');
  for (const [name, spans, count] of [
    ['source', doc.sourceSpans, doc.source.frameCount], ['output', doc.clips, doc.output.frameCount],
  ] as const) {
    let cursor = 0;
    const ids = new Set<string>();
    for (const s of spans) {
      if (ids.has(s.id)) errors.push(`${name} duplicate id`);
      ids.add(s.id);
      if (!Number.isSafeInteger(s.startFrame) || !Number.isSafeInteger(s.endFrame) || s.endFrame <= s.startFrame) errors.push(`${name} invalid frame span`);
      if (s.startFrame !== cursor) errors.push(`${name} spans must be contiguous`);
      cursor = s.endFrame;
    }
    if (cursor !== count || !Number.isSafeInteger(count) || count <= 0) errors.push(`${name} frame coverage does not match declared duration`);
  }
  let previousSourceEnd = 0;
  for (const c of doc.clips) if (c.kind === 'speech') {
    if (c.sourceStartFrame === undefined || c.sourceEndFrame === undefined
      || c.sourceStartFrame < 0 || c.sourceEndFrame > doc.source.frameCount) errors.push('speech source bounds invalid');
    // Independent rounding at source and output can differ by one frame.
    if (Math.abs((c.sourceEndFrame! - c.sourceStartFrame!) - (c.endFrame - c.startFrame)) > 1) errors.push('speech duration changed');
    if (c.sourceStartFrame! < previousSourceEnd) errors.push('speech source order changed or source reused');
    previousSourceEnd = c.sourceEndFrame!;
  }
  const sourceIds = new Set(doc.sourceSpans.map(s => s.id));
  const clipIds = new Set(doc.clips.map(c => c.id));
  const relationIds = new Set<string>();
  for (const r of doc.relations) {
    if (relationIds.has(r.id)) errors.push('duplicate relation id');
    relationIds.add(r.id);
    if (r.sourceSpanIds.some(id => !sourceIds.has(id)) || r.outputClipIds.some(id => !clipIds.has(id))) errors.push('relation refers to missing subject');
    if (r.exact !== (r.kind === 'copy')) errors.push('only copied audio has exact correspondence');
    if (r.kind === 'omit' && r.outputClipIds.length) errors.push('omitted source has an output');
    if (r.kind === 'insert' && r.sourceSpanIds.length) errors.push('generated insertion has a source');
    if (r.kind === 'copy') {
      const clip = doc.clips.find(c => c.id === r.outputClipIds[0]);
      if (r.outputClipIds.length !== 1 || !r.sourceSpanIds.length || clip?.kind !== 'speech') errors.push('copy must relate source to a speech clip');
      for (const id of r.sourceSpanIds) {
        const source = doc.sourceSpans.find(s => s.id === id);
        if (source && clip && (source.startFrame < clip.sourceStartFrame! || source.endFrame > clip.sourceEndFrame!)) errors.push('copy relation escapes source bounds');
      }
    }
  }
  for (const s of doc.sourceSpans) if (!doc.relations.some(r => r.sourceSpanIds.includes(s.id))) errors.push('unmapped source region');
  for (const c of doc.clips) if (!doc.relations.some(r => r.outputClipIds.includes(c.id))) errors.push('unmapped output clip');
  return errors;
}

/** Bounded runtime boundary for captured JSON; a digest is verified, never trusted by shape. */
export async function parseAssembly(value: unknown): Promise<AssemblyDocument> {
  const fail = (detail: string): never => { throw new Error(`Invalid assembly: ${detail}`); };
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail('expected document');
  const doc = value as AssemblyDocument;
  if (doc.schemaVersion !== 1 || doc.recipe !== 'anchored-assembly-v1') fail('unsupported version or recipe');
  if (typeof doc.digest !== 'string' || !/^sha256:[a-f0-9]{64}$/.test(doc.digest)) fail('invalid digest');
  if (!doc.release || typeof doc.release.id !== 'string' || doc.release.id.length > 4096) fail('invalid release');
  if (!doc.source || !doc.output || doc.source.frameRate !== RATE || doc.output.frameRate !== RATE) fail('unsupported frame time base');
  for (const key of ['sourceSpans', 'clips', 'relations'] as const) {
    if (!Array.isArray(doc[key]) || doc[key].length > 100_000) fail(`invalid ${key}`);
  }
  for (const s of doc.sourceSpans) {
    if (!s || typeof s.id !== 'string' || typeof s.reason !== 'string'
      || !['copied', 'replaced', 'omitted'].includes(s.disposition)
      || !['speech-region', 'voice-region', 'quiet-region', 'other-region', 'unknown'].includes(s.classification)) fail('invalid source span');
  }
  for (const c of doc.clips) if (!c || typeof c.id !== 'string' || !['speech', 'tone'].includes(c.kind)) fail('invalid output clip');
  for (const r of doc.relations) {
    if (!r || typeof r.id !== 'string' || !['copy', 'omit', 'replace', 'insert'].includes(r.kind)
      || !Array.isArray(r.sourceSpanIds) || !Array.isArray(r.outputClipIds)
      || r.sourceSpanIds.length > 100_000 || r.outputClipIds.length > 100_000) fail('invalid relation');
  }
  try {
    // relative-url-ok: schema validation wrapper only; no request uses this placeholder base.
    parseManifest({ version: 1, generatedAt: '2000-01-01T00:00:00Z', assetBase: '/audio',
      recordings: [doc.recording], themes: [], stories: [] });
  } catch { fail('invalid pinned recording'); }
  if (!doc.plan || doc.plan.recordingId !== doc.recordingId || doc.recording.id !== doc.recordingId
    || !Array.isArray(doc.plan.timeline) || !Number.isFinite(doc.plan.actualSec)) fail('invalid pinned plan');
  const errors = validateAssembly(doc);
  if (errors.length) fail(errors.join('; '));
  const { digest, ...body } = doc;
  if (await contentDigest(body) !== digest) fail('digest mismatch');
  return structuredClone(doc);
}

/** Returns every counterpart; generated/replaced audio deliberately has no source sample cursor. */
export function counterpartsAt(doc: AssemblyDocument, lane: 'source' | 'output', atFrame: number): Counterpart[] {
  const spans = lane === 'source' ? doc.sourceSpans : doc.clips;
  const selected = spans.filter(s => s.startFrame <= atFrame && atFrame < s.endFrame);
  const ids = new Set(selected.map(s => s.id));
  return doc.relations.filter(r => (lane === 'source' ? r.sourceSpanIds : r.outputClipIds).some(id => ids.has(id)))
    .filter(r => {
      if (lane === 'source' || r.kind !== 'copy') return true;
      const clip = doc.clips.find(c => r.outputClipIds.includes(c.id));
      const sourceFrame = clip ? clip.sourceStartFrame! + atFrame - clip.startFrame : -1;
      return doc.sourceSpans.some(s => r.sourceSpanIds.includes(s.id) && s.startFrame <= sourceFrame && sourceFrame < s.endFrame);
    })
    .map(r => {
      const clip = doc.clips.find(c => r.outputClipIds.includes(c.id));
      const mapped = r.kind === 'copy' && clip ? lane === 'source'
        ? clip.startFrame + atFrame - clip.sourceStartFrame!
        : clip.sourceStartFrame! + atFrame - clip.startFrame : null;
      return { relationId: r.id, kind: r.kind, frame: mapped,
        subjectIds: lane === 'source' ? r.outputClipIds : r.sourceSpanIds };
    });
}
